import { OrderItem } from "@dining/api-client";
import { Order } from "@dining/api-client";

export const handleReorder = async ({
  selected,
  orderId,
  reOrder,
  reOrderConsent,
  setLoading,
  setDialogOpen,
  setDialogTitle,
  setDialogMessage,
  setDialogAction,
  setDialogButtonText,
  getCart,
  navigate,
}: {
  selected: Order | undefined;
  orderId?: string | undefined;
  reOrder: (orderId: string, reOrderConsent: boolean) => Promise<Order>;
  reOrderConsent: boolean;
  setLoading: (loading: boolean) => void;
  setDialogOpen: (open: boolean) => void;
  setDialogTitle: (title: string) => void;
  setDialogMessage: (message: string | JSX.Element) => void;
  setDialogAction: (action: () => void) => void;
  setDialogButtonText: (text: string) => void;
  getCart: () => void;
  navigate: (path: string, options?: { state?: { items: OrderItem[] } }) => void;
}) => {
  if (!selected) return;
  setLoading(true);
  try {
    if (!orderId) {
      throw new Error("Order ID is undefined");
    }
    const response = await reOrder(orderId, reOrderConsent);
    const cafeId = response?.cafeId;
    const items = response?.items;

    const validItems = items.filter((item: OrderItem) => item.issueType === "None");
    const invalidItems = items.filter((item: OrderItem) => item.issueType === "ItemInactive");
    const priceMismatchedItems = items.filter(
      (item: OrderItem) =>
        item.issueType === "ItemPriceMismatched" || item.issueType === "ModifierPriceMismatched",
    );
    const invalidModifiers = items.filter(
      (item: OrderItem) => item.issueType === "ItemWithInvalidModifier",
    );

    const allCheckoutItems = [...validItems, ...priceMismatchedItems];

    const removeItemsDialog = () => {
      const dialogQueue: { title: string; message: string; action: () => Promise<void> }[] = [];
      if (priceMismatchedItems.length > 0) {
        const priceMismatchedItemNames = formatDialogMessage(priceMismatchedItems);
        const verb = priceMismatchedItems.length > 1 ? "have" : "has";
        dialogQueue.push({
          title: `There has been a change in the price of your order.`,
          message: `${priceMismatchedItemNames} ${verb} changed in price. Please confirm that you would like to place a reorder.`,
          action: async () => {
            reOrderConsent = true;
          },
        });
      }
      if (invalidItems.length > 0) {
        const invalidItemNames = formatDialogMessage(invalidItems);
        const verb = invalidItems.length > 1 ? "are" : "is";
        dialogQueue.push({
          title: `Some items are not available`,
          message: `${invalidItemNames} ${verb} not available today and will not be added to your order.`,
          action: async () => {
            reOrderConsent = true;
          },
        });
      }
      if (invalidModifiers.length > 0) {
        const invalidModifierNames = formatDialogMessage(invalidModifiers);
        const verb = invalidModifiers.length > 1 ? "have" : "has";
        dialogQueue.push({
          title: "Some items need review",
          message: `${invalidModifierNames} ${verb} unavailable modifiers that will not be added to your order.`,
          action: async () => {
            reOrderConsent = true;
          },
        });
      }

      const showNextDialog = async () => {
        if (dialogQueue.length === 0) {
          await reOrder(orderId, reOrderConsent);
          getCart();
          navigate(`/cafe/${cafeId}/checkout`, {
            state: {
              items: allCheckoutItems,
            },
          });
          return;
        }
        const { title, message, action } = dialogQueue.shift()!;
        setDialogTitle(title);
        setDialogMessage(message);
        setDialogAction(() => async () => {
          await action();
          showNextDialog();
        });
        setDialogButtonText("Continue");
        setDialogOpen(true);
      };

      showNextDialog();
    };
    if (validItems.length === items.length) {
      getCart();
      navigate(`/cafe/${cafeId}/checkout`);
    } else if (
      ((invalidItems.length > 0 || invalidModifiers.length > 0) &&
        (validItems.length > 0 || priceMismatchedItems.length > 0)) ||
      priceMismatchedItems.length > 0
    ) {
      removeItemsDialog();
    } else {
      const allInvalidItems = [...invalidModifiers, ...invalidItems];
      const verb = allInvalidItems.length > 1 ? "are" : "is";
      setDialogTitle(`Some items you previously ordered ${verb} not available:`);
      const invalidItemsMessage = allInvalidItems.map((item) => `• ${item.name}`).join("\n");
      const fullMessage = `${invalidItemsMessage} \n Browse the menu to select other items.`;
      setDialogMessage(fullMessage);
      setDialogAction(() => () => {
        setDialogOpen(false);
        navigate(`/cafe/${cafeId}`);
      });
      setDialogButtonText("Browse menu");
      setDialogOpen(true);
    }
  } catch (error) {
    console.error("Reorder failed:", error);
  } finally {
    setLoading(false);
  }
};

const formatDialogMessage = (items: { name: string }[]) => {
  if (items.length === 2) {
    return `${items[0].name} and ${items[1].name}`;
  } else if (items.length > 2) {
    const allButLastItem = items
      .slice(0, -1)
      .map((item) => item.name)
      .join(", ");
    const lastItem = items[items.length - 1].name;
    return `${allButLastItem} and ${lastItem}`;
  }
  return items[0].name;
};
